import { useState, type FormEvent } from 'react';
import { Calendar, Plus, Trash2, Save, Loader2, Power, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useCreateSchedule,
  useDeleteSchedule,
  useSchedules,
  useUpdateSchedule,
} from '@/hooks/useSchedules';
import { usePresets } from '@/hooks/usePresets';
import { useSites } from '@/hooks/useSites';
import { ApiError } from '@/lib/api-client';
import { cn, formatDate } from '@/lib/utils';

const CRON_PRESETS: Array<{ label: string; value: string }> = [
  { label: 'Her gün saat 09:00', value: '0 9 * * *' },
  { label: 'Her gün saat 09:00 ve 18:00', value: '0 9,18 * * *' },
  { label: 'Her 4 saatte', value: '0 */4 * * *' },
  { label: 'Pazartesi-Cuma 10:00', value: '0 10 * * 1-5' },
  { label: 'Her pazar 20:00', value: '0 20 * * 0' },
];

export function SchedulePage() {
  const { data, isPending } = useSchedules();
  const sites = useSites();
  const presets = usePresets();
  const create = useCreateSchedule();
  const update = useUpdateSchedule();
  const del = useDeleteSchedule();

  const [name, setName] = useState('');
  const [siteId, setSiteId] = useState('');
  const [presetId, setPresetId] = useState('');
  const [cron, setCron] = useState('0 9 * * *');
  const [postsPerRun, setPostsPerRun] = useState(1);
  const [autoDispatch, setAutoDispatch] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        site_id: siteId,
        preset_id: presetId || null,
        name,
        cron_expression: cron,
        posts_per_run: postsPerRun,
        auto_dispatch: autoDispatch,
        enabled: true,
      });
      setName('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kaydedilemedi');
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Calendar className="w-5 h-5" /> Zamanlamalar
        </h1>
        <p className="text-muted-foreground text-sm">
          Cron bazlı otomatik içerik üretimi. Worker her dakika kontrol eder.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Plus className="w-4 h-4" /> Yeni Zamanlama
          </CardTitle>
          <CardDescription>Seçili site için sabit aralıklarla içerik üretir.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>İsim</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="Örn: Günlük 2 makale"
                />
              </div>
              <div className="space-y-2">
                <Label>Site</Label>
                <select
                  value={siteId}
                  onChange={(e) => setSiteId(e.target.value)}
                  required
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">Seç…</option>
                  {sites.data?.sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Preset (opsiyonel)</Label>
                <select
                  value={presetId}
                  onChange={(e) => setPresetId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">— Default ayarlar —</option>
                  {presets.data?.presets.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Her çalışmada post sayısı</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={postsPerRun}
                  onChange={(e) => setPostsPerRun(Number(e.target.value))}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Cron Expression</Label>
                <Input
                  value={cron}
                  onChange={(e) => setCron(e.target.value)}
                  className="font-mono text-xs"
                  placeholder="0 9 * * *"
                />
                <div className="flex flex-wrap gap-1">
                  {CRON_PRESETS.map((p) => (
                    <Button
                      key={p.value}
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setCron(p.value)}
                      className="text-xs"
                    >
                      {p.label}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 sm:col-span-2">
                <input
                  id="ad"
                  type="checkbox"
                  checked={autoDispatch}
                  onChange={(e) => setAutoDispatch(e.target.checked)}
                  className="h-4 w-4 rounded border-input"
                />
                <Label htmlFor="ad" className="cursor-pointer">
                  Otomatik dispatch (üretilen içerik otomatik WP'ye gönderilsin)
                </Label>
              </div>
            </div>
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                {error}
              </div>
            )}
            <Button type="submit" disabled={create.isPending || !siteId || !name}>
              {create.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Ekle
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Mevcut Zamanlamalar</CardTitle>
        </CardHeader>
        <CardContent>
          {isPending ? (
            <div className="text-muted-foreground">Yükleniyor…</div>
          ) : data?.schedules.length === 0 ? (
            <p className="text-sm text-muted-foreground">Henüz zamanlama yok.</p>
          ) : (
            <div className="space-y-2">
              {data?.schedules.map((s) => (
                <div key={s.id} className={cn('rounded-md border p-3 flex items-center gap-3', !s.enabled && 'opacity-60')}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{s.name}</span>
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">{s.cron_expression}</code>
                      {s.auto_dispatch && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
                          AUTO DISPATCH
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Site: {s.site_name} · {s.posts_per_run} post/çalışma
                      {s.last_run_at && ` · Son çalışma: ${formatDate(s.last_run_at)}`}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      update.mutate({ id: s.id, body: { enabled: !s.enabled } as never })
                    }
                  >
                    <Power className={cn('w-3.5 h-3.5', s.enabled && 'text-emerald-600 dark:text-emerald-400')} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => del.mutate(s.id)} disabled={del.isPending}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardContent className="py-4 text-xs text-muted-foreground flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
          Worker cron trigger her dakika çalışır. Due olan zamanlamaları tarar, Queue'ya iş bırakır,
          Queue consumer arka planda üretim yapar.
        </CardContent>
      </Card>
    </div>
  );
}
